import { useState } from 'react';

import { useNpiSuggestionsBackend } from './NpiSuggestionsGatewayProvider';
import {
  getNpiSuggestionRunQueryOptions,
  getNpiSuggestionsQueryOptions,
} from '../api/queryOptions';
import { laterStage } from '../model/progress';
import type { NpiRunEvent } from '../model/runEvents';
import {
  NO_SUGGESTIONS,
  type NpiSuggestionStage,
  type NpiSuggestions,
  type NpiSuggestionsRequest,
} from '../model/types';
import type { NpiRunConnection } from '../ports/npiRunChannel';

import { useUserScopedQuery } from '@/hooks/useUserScopedQuery';

export type NpiSuggestionsState =
  | { phase: 'idle' }
  | { phase: 'running'; stage: NpiSuggestionStage | null }
  | { phase: 'done'; suggestions: NpiSuggestions };

/** How often a run is read back while the realtime channel is not live. */
export const RUN_POLL_MS = 2_000;

/**
 * Safety net while the channel is live: a hint lost in flight costs at most
 * this long, never a run the screen waits on forever.
 */
export const RUN_POLL_WHILE_LIVE_MS = 10_000;

/**
 * Suggestions for one request, followed while the server works them out.
 *
 * The start returns at once. While the run works, its stages arrive as
 * realtime hints and the run is read back from the API, which stays the one
 * source: a completion hint re-reads it, a reconnect re-reads it, and the read
 * is polled every couple of seconds whenever the channel is not live. Nothing
 * here gives up on a run; the server finishes every run, with no candidates
 * if it must. A failed start, or a read that still fails after its retries,
 * reads as no suggestions, so the screen moves on to asking for a name rather
 * than showing an error the person cannot act on.
 */
export function useNpiSuggestions(
  request: NpiSuggestionsRequest,
  { enabled }: { enabled: boolean }
): NpiSuggestionsState {
  const { gateway, channel } = useNpiSuggestionsBackend();

  const [connection, setConnection] = useState<NpiRunConnection>('down');

  const [hintedStage, setHintedStage] = useState<{
    runId: string;
    stage: NpiSuggestionStage;
  } | null>(null);

  const start = useUserScopedQuery(getNpiSuggestionsQueryOptions, [request, gateway], {
    enabled,
  });

  const started = start.data;

  const runningId = enabled && started?.status === 'running' ? started.runId : null;

  const run = useUserScopedQuery(getNpiSuggestionRunQueryOptions, [runningId ?? '', gateway], {
    enabled: runningId !== null,
    refetchInterval: (query) => pollInterval(query.state.data?.status, connection),
  });

  channel.useRunEvents({
    runId: runningId,
    onEvent: (event: NpiRunEvent) => {
      if (event.type === 'progress') {
        setHintedStage({ runId: event.runId, stage: event.stage });

        return;
      }

      run.refetch().catch(() => {});
    },
    onResume: () => {
      run.refetch().catch(() => {});
    },
    onConnectionChange: setConnection,
  });

  if (!enabled) {
    return { phase: 'idle' };
  }

  if (start.isError) {
    return { phase: 'done', suggestions: NO_SUGGESTIONS };
  }

  if (!started) {
    return { phase: 'running', stage: null };
  }

  if (started.status === 'done') {
    return { phase: 'done', suggestions: started.suggestions };
  }

  const read = run.data;

  if (read?.status === 'done') {
    return { phase: 'done', suggestions: read.suggestions };
  }

  if (read === null || run.isError) {
    return { phase: 'done', suggestions: NO_SUGGESTIONS };
  }

  const hinted = hintedStage?.runId === started.runId ? hintedStage.stage : null;

  return { phase: 'running', stage: laterStage(hinted, read?.stage ?? started.stage) };
}

function pollInterval(
  status: 'running' | 'done' | undefined,
  connection: NpiRunConnection
): number | false {
  if (status === 'done') {
    return false;
  }

  return connection === 'live' ? RUN_POLL_WHILE_LIVE_MS : RUN_POLL_MS;
}
